import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { getAppLogo } from '../utils/usedApps';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  Wrench,
  Storefront,
  Buildings,
  Truck,
  Heartbeat,
  Sparkle,
  Lightning,
  Robot,
  CheckCircle,
  CircleNotch,
  Broom,
  Scissors,
  Car,
  GraduationCap,
  MagnifyingGlass,
  XCircle,
  PlusCircle,
  Check
} from '@phosphor-icons/react';

/* ═══════════════════════════════════════════════════════
   BUSINESS TYPES
   ═══════════════════════════════════════════════════════ */
const BUSINESS_TYPES = [
  { id: 'trades',      label: 'Trades & Home Services',  icon: Wrench,          examples: 'Plumbing, Electrical, HVAC, Roofing' },
  { id: 'professional', label: 'Professional Services',   icon: Buildings,       examples: 'Accounting, Legal, Consulting, IT' },
  { id: 'health',      label: 'Health & Wellness',        icon: Heartbeat,       examples: 'Clinic, Physio, Dental, Chiro' },
  { id: 'retail',      label: 'Retail & Hospitality',     icon: Storefront,      examples: 'Restaurant, Café, Retail, Events' },
  { id: 'auto',        label: 'Automotive',               icon: Car,             examples: 'Mechanic, Detailing, Towing, Auto body' },
  { id: 'beauty',      label: 'Beauty & Personal Care',   icon: Scissors,        examples: 'Salon, Barber, Spa, Beauty' },
  { id: 'cleaning',    label: 'Cleaning & Maintenance',   icon: Broom,           examples: 'Commercial cleaning, Pest control, Landscaping' },
  { id: 'logistics',   label: 'Logistics & Delivery',     icon: Truck,           examples: 'Courier, Moving, Freight, Transport' },
  { id: 'education',   label: 'Education & Training',     icon: GraduationCap,   examples: 'Tutoring, Coaching, Training, Courses' },
  { id: 'other',       label: 'Something else',           icon: Sparkle,         examples: 'Tell us and we\'ll find the best workers' },
];

/* ═══════════════════════════════════════════════════════
   GOALS
   ═══════════════════════════════════════════════════════ */
const GOALS = [
  { id: 'never-miss-lead',     label: 'Never miss a lead',              desc: 'Respond instantly to every enquiry' },
  { id: 'book-jobs',           label: 'Book jobs automatically',        desc: 'Turn enquiries into confirmed bookings' },
  { id: 'follow-up',          label: 'Follow up every lead',           desc: 'Automatically chase and convert more work' },
  { id: 'website-enquiries',  label: 'Capture website enquiries',      desc: 'Turn website visitors into real jobs' },
  { id: 'reactivate',         label: 'Reactivate old customers',       desc: 'Win back past customers automatically' },
  { id: 'stay-organised',     label: 'Stay organised (jobs & comms)',  desc: 'Keep jobs, messages, and updates in sync' },
];

/* ═══════════════════════════════════════════════════════
   WORKER RECOMMENDATIONS BY GOAL
   ═══════════════════════════════════════════════════════ */
const WORKER_RECS = {
  'never-miss-lead': [
    { name: 'Answer enquiries', intent: 'Handle new customer enquiries and draft professional replies', primary: true,  headline: 'Respond to every new enquiry instantly' },
    { name: 'Route urgent issues', intent: 'Detect urgent customer issues and escalate to the team', primary: false, headline: 'Never miss an urgent issue' },
    { name: 'Qualify new leads', intent: 'Qualify new inbound leads and prioritize high-value ones', primary: false, headline: 'Focus on leads that matter' },
  ],
  'book-jobs': [
    { name: 'Booking assistant', intent: 'Help customers book appointments and check availability', primary: true,  headline: 'Turn enquiries into booked jobs automatically' },
    { name: 'Answer enquiries', intent: 'Handle new customer enquiries and draft professional replies', primary: false, headline: 'Respond to every enquiry instantly' },
    { name: 'Send reminders', intent: 'Send booking confirmation and appointment reminders to customers', primary: false, headline: 'Reduce no-shows automatically' },
  ],
  'follow-up': [
    { name: 'Follow up missed leads', intent: 'Follow up with leads who didn\'t respond after initial contact', primary: true,  headline: 'Never let a lead go cold' },
    { name: 'Follow up on quotes', intent: 'Follow up with customers who received a quote but haven\'t responded', primary: false, headline: 'Turn quotes into confirmed jobs' },
    { name: 'Qualify new leads', intent: 'Qualify new inbound leads and prioritize high-value ones', primary: false, headline: 'Focus on leads that matter' },
  ],
  'website-enquiries': [
    { name: 'Website enquiry handler', intent: 'Capture and respond to website form submissions and enquiries instantly', primary: true,  headline: 'Turn every website visitor into a real lead' },
    { name: 'Booking assistant', intent: 'Help customers book appointments and check availability', primary: false, headline: 'Let visitors book directly from your site' },
    { name: 'Follow up missed leads', intent: 'Follow up with leads who didn\'t respond after initial contact', primary: false, headline: 'Chase up visitors who didn\'t convert' },
  ],
  'reactivate': [
    { name: 'Win-back campaign', intent: 'Re-engage past customers with personalised follow-ups and offers', primary: true,  headline: 'Bring back customers who haven\'t booked in a while' },
    { name: 'Request reviews', intent: 'Send review requests to customers after completing a job', primary: false, headline: 'Build your reputation on autopilot' },
    { name: 'Answer enquiries', intent: 'Handle new customer enquiries and draft professional replies', primary: false, headline: 'Respond to every enquiry instantly' },
  ],
  'stay-organised': [
    { name: 'Job tracker', intent: 'Keep track of active jobs, messages, and customer updates in one place', primary: true,  headline: 'Never lose track of a job again' },
    { name: 'Summarize emails', intent: 'Summarize long email threads and extract key action items', primary: false, headline: 'Handle every email without lifting a finger' },
    { name: 'Send reminders', intent: 'Send booking confirmation and appointment reminders to customers', primary: false, headline: 'Reduce no-shows automatically' },
  ],
};

/* ═══════════════════════════════════════════════════════
   TOOL SUGGESTIONS BY BUSINESS TYPE
   ═══════════════════════════════════════════════════════ */
const BIZ_TOOL_MAP = {
  trades:       ['Jobber', 'ServiceM8', 'Xero', 'QuickBooks', 'Google Calendar', 'Gmail'],
  professional: ['HubSpot', 'Google Calendar', 'Zoom', 'Slack', 'Google Drive', 'Gmail'],
  health:       ['Calendly', 'Google Calendar', 'Mailchimp', 'Stripe', 'Gmail'],
  retail:       ['Stripe', 'Mailchimp', 'Google Sheets', 'Google Calendar', 'Gmail'],
  auto:         ['Jobber', 'Xero', 'Google Calendar', 'Stripe', 'Gmail'],
  beauty:       ['Calendly', 'Google Calendar', 'Mailchimp', 'Stripe', 'Gmail'],
  cleaning:     ['Jobber', 'Google Calendar', 'Xero', 'Mailchimp', 'Gmail'],
  logistics:    ['Google Sheets', 'Google Calendar', 'Slack', 'Airtable', 'Gmail'],
  education:    ['Calendly', 'Google Calendar', 'Zoom', 'Mailchimp', 'Gmail'],
  other:        ['Gmail', 'Google Calendar', 'Stripe', 'Google Sheets', 'Slack'],
};

const GOAL_TOOL_MAP = {
  'never-miss-lead':    ['Gmail', 'Outlook', 'HubSpot', 'Slack', 'Google Sheets', 'WhatsApp'],
  'book-jobs':          ['Google Calendar', 'Calendly', 'Outlook', 'Gmail', 'ServiceM8', 'Jobber'],
  'follow-up':          ['Gmail', 'Outlook', 'HubSpot', 'Mailchimp', 'Pipedrive', 'Slack'],
  'website-enquiries':  ['Gmail', 'HubSpot', 'Google Sheets', 'Slack', 'Airtable', 'Zapier'],
  'reactivate':         ['Mailchimp', 'Gmail', 'HubSpot', 'Google Sheets', 'Stripe'],
  'stay-organised':     ['Google Calendar', 'Slack', 'Google Sheets', 'Airtable', 'Google Drive', 'Notion'],
};

const LOCAL_APP_CATALOG = [
  'Gmail', 'Outlook', 'Google Calendar', 'Calendly', 'Slack', 'Google Sheets',
  'Google Drive', 'HubSpot', 'Salesforce', 'Pipedrive', 'Xero', 'QuickBooks',
  'Stripe', 'Mailchimp', 'Jobber', 'ServiceM8', 'Housecall Pro', 'Airtable',
  'WhatsApp', 'Twilio', 'Zoom', 'Notion', 'Asana', 'Trello', 'Jira',
  'Shopify', 'Zendesk', 'Freshdesk', 'Intercom', 'Discord', 'Microsoft Teams',
  'Dropbox', 'Linear', 'GitHub', 'Google Business Profile', 'Trustpilot',
  'Facebook', 'Instagram', 'Zapier', 'PandaDoc', 'MYOB', 'Tradify',
  'Fergus', 'ServiceTitan', 'simPRO', 'FieldPulse', 'ZenMaid', 'AccuLynx',
  'JobNimbus', 'Zoho', 'ActiveCampaign', 'Constant Contact', 'SendGrid',
  'Typeform', 'Monday.com', 'ClickUp', 'Basecamp', 'Copper',
];

const isTestTool = (name) => /^test[_\s]/i.test(name) || /test$/i.test(name) || name.includes('TEST_');

/* ═══════════════════════════════════════════════════════
   ONBOARDING WIZARD COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function OnboardingWizard({ onComplete }) {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const [step, setStep] = useState(0); // 0=business, 1=goal, 2=tools, 3=recommendations
  const [businessType, setBusinessType] = useState(null);
  const [goal, setGoal] = useState(null);
  const [creating, setCreating] = useState(null);
  const [created, setCreated] = useState([]);

  // Tools step state
  const [selectedTools, setSelectedTools] = useState(new Set());
  const [toolSearch, setToolSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalApps, setTotalApps] = useState(0);
  const searchTimerRef = useRef(null);

  const recommendations = goal ? (WORKER_RECS[goal] || WORKER_RECS['never-miss-lead']) : [];

  // Build suggested tools from business type + goal
  const suggestedTools = React.useMemo(() => {
    const bizTools = BIZ_TOOL_MAP[businessType] || BIZ_TOOL_MAP.other;
    const goalTools = GOAL_TOOL_MAP[goal] || [];
    return [...new Set([...bizTools, ...goalTools])];
  }, [businessType, goal]);

  const handleToolToggle = (tool) => {
    setSelectedTools(prev => {
      const next = new Set(prev);
      next.has(tool) ? next.delete(tool) : next.add(tool);
      return next;
    });
  };

  const handleToolSearchChange = useCallback((query) => {
    setToolSearch(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    const q = query.trim().toLowerCase();
    const localMatches = LOCAL_APP_CATALOG
      .filter(name => name.toLowerCase().includes(q))
      .map(name => ({ name, slug: name.toLowerCase().replace(/\s+/g, ''), logo: getAppLogo(name) }));
    setSearchResults(localMatches.slice(0, 20));
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/service-os/integrations/apps?q=${encodeURIComponent(query.trim())}`,
          {},
          orgId
        );
        if (res.ok) {
          const data = await res.json();
          const apiApps = (data.apps || []).slice(0, 30);
          if (data.total_apps) setTotalApps(data.total_apps);
          const apiSlugs = new Set(apiApps.map(a => a.slug));
          const merged = [...apiApps, ...localMatches.filter(l => !apiSlugs.has(l.slug))].slice(0, 30);
          setSearchResults(merged);
        }
      } catch { /* local results stay */ }
      finally { setSearchLoading(false); }
    }, 300);
  }, [orgId]);

  // Save tools to org tool stack and advance to recommendations
  const handleToolsContinue = async () => {
    if (selectedTools.size > 0 && orgId) {
      try {
        await fetchWithOrg(`${BACKEND_URL}/api/service-os/tool-stack/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tools: Array.from(selectedTools) }),
        }, orgId);
      } catch { /* non-blocking */ }
    }
    setStep(3);
  };

  const handleCreateWorker = async (rec, index) => {
    if (created.includes(index)) return;
    setCreating(index);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/command/create-worker`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ intent: rec.intent }),
        },
        orgId
      );
      if (res.ok) {
        setCreated(prev => [...prev, index]);
        toast.success(`${rec.name} worker created`);
      } else {
        toast.error('Failed to create worker');
      }
    } catch {
      toast.error('Error creating worker');
    } finally {
      setCreating(null);
    }
  };

  const handleFinish = () => {
    localStorage.setItem('mm_onboarding_done', 'true');
    if (onComplete) onComplete();
    navigate('/workers');
  };

  const handleSkip = () => {
    localStorage.setItem('mm_onboarding_done', 'true');
    if (onComplete) onComplete();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto" style={{ background: 'radial-gradient(circle at top, #101722 0%, #0B0F14 60%)' }} data-testid="onboarding-wizard">
      <div className="w-full max-w-[960px] mx-auto px-6 py-12">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 justify-center">
          {[0, 1, 2, 3].map(s => (
            <div
              key={s}
              className={`h-1 rounded-full transition-all duration-300 ${
                s <= step ? 'bg-[#00E5C0] w-12' : 'bg-[rgba(255,255,255,0.08)] w-6'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* ═══════ STEP 0: Business Type ═══════ */}
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-[#E5E7EB] mb-2">What does your business do?</h1>
                <p className="text-sm text-[#9CA3AF]">We'll recommend the best workers for your industry</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {BUSINESS_TYPES.map(bt => {
                  const Icon = bt.icon;
                  const selected = businessType === bt.id;
                  return (
                    <button
                      key={bt.id}
                      onClick={() => { setBusinessType(bt.id); setStep(1); }}
                      data-testid={`biz-${bt.id}`}
                      className={`text-left p-4 rounded-[14px] border transition-all duration-200 ${
                        selected
                          ? 'bg-[#1E2D24] border-[#34D399] text-[#34D399] shadow-[0_0_0_1px_rgba(52,211,153,0.25)]'
                          : 'bg-[#1A2230] border-[rgba(255,255,255,0.08)] text-white hover:bg-[#202A3B] hover:border-[rgba(255,255,255,0.12)] hover:-translate-y-[2px]'
                      }`}
                    >
                      <Icon size={20} weight="duotone" className={selected ? 'text-[#34D399]' : 'text-[#9CA3AF]'} />
                      <p className="font-medium text-sm mt-2">{bt.label}</p>
                      <p className="text-[10px] text-[#7D8896] mt-0.5">{bt.examples}</p>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleSkip}
                className="mt-6 text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors mx-auto block"
              >
                Skip for now
              </button>
            </motion.div>
          )}

          {/* ═══════ STEP 1: Goal ═══════ */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-[#E5E7EB] mb-2">What's your biggest bottleneck?</h1>
                <p className="text-sm text-[#9CA3AF]">Pick the one that costs you the most time or money</p>
              </div>

              <div className="space-y-3">
                {GOALS.map(g => {
                  const selected = goal === g.id;
                  return (
                    <button
                      key={g.id}
                      onClick={() => { setGoal(g.id); setStep(2); }}
                      data-testid={`goal-${g.id}`}
                      className={`w-full text-left p-4 rounded-[14px] border transition-all duration-200 flex items-center gap-4 ${
                        selected
                          ? 'bg-[#1E2D24] border-[#34D399] shadow-[0_0_0_1px_rgba(52,211,153,0.25)]'
                          : 'bg-[#1A2230] border-[rgba(255,255,255,0.08)] hover:bg-[#202A3B] hover:border-[rgba(255,255,255,0.12)] hover:-translate-y-[2px]'
                      }`}
                    >
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{g.label}</p>
                        <p className="text-[11px] text-[#7D8896] mt-0.5">{g.desc}</p>
                      </div>
                      <ArrowRight size={16} className="text-[#7D8896]" />
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setStep(0)}
                className="mt-6 text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors mx-auto flex items-center gap-1"
              >
                <ArrowLeft size={12} /> Back
              </button>
            </motion.div>
          )}

          {/* ═══════ STEP 2: Your Tools ═══════ */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-[#E5E7EB] mb-2">Which tools do you use?</h1>
                <p className="text-sm text-[#9CA3AF]">Select the apps your business runs on — they'll be ready when you build workers.</p>
              </div>

              {/* Selected tools */}
              {selectedTools.size > 0 && (
                <>
                  <p className="text-xs text-[#E5E7EB] font-semibold uppercase tracking-[0.08em] mb-3">Your tools</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-6" data-testid="onboarding-selected-tools">
                    {[...selectedTools].map((tool) => (
                      <button key={tool} data-testid={`onb-tool-selected-${tool.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => handleToolToggle(tool)}
                        className="group flex items-center gap-3 rounded-xl border border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)] px-3.5 py-3 transition-all hover:bg-[rgba(0,229,192,0.08)]">
                        <img src={getAppLogo(tool)} alt={tool} className="w-7 h-7 rounded-md object-contain"
                          onError={(e) => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
                        <span className="text-sm font-medium text-[#F0F4F8] flex-1 text-left truncate">{tool}</span>
                        <XCircle size={16} weight="fill" className="text-[#5C6778] group-hover:text-red-400 shrink-0 transition-colors" />
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Suggested tools */}
              {(() => {
                const unselected = suggestedTools.filter(t => !selectedTools.has(t));
                if (unselected.length === 0) return null;
                return (
                  <>
                    <p className="text-xs text-[#7D8896] font-medium uppercase tracking-[0.08em] mb-2.5">Suggested for your business</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-5">
                      {unselected.map((tool) => (
                        <button key={tool} data-testid={`onb-tool-suggest-${tool.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => handleToolToggle(tool)}
                          className="flex flex-col items-center gap-1.5 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A2230] p-2.5 transition-all hover:border-[rgba(255,255,255,0.12)] hover:bg-[#202A3B]">
                          <img src={getAppLogo(tool)} alt={tool} className="w-5 h-5 rounded object-contain opacity-60"
                            onError={(e) => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
                          <span className="text-[10px] text-[#7D8896] text-center leading-tight">{tool}</span>
                        </button>
                      ))}
                    </div>
                  </>
                );
              })()}

              {/* Search */}
              <div className="relative mb-3">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6778]" />
                <input
                  data-testid="onb-tool-search-input"
                  type="text" value={toolSearch} onChange={(e) => handleToolSearchChange(e.target.value)}
                  placeholder="Search 1,000+ apps (Gmail, Xero, ServiceM8, HubSpot...)"
                  className="w-full pl-8 pr-3 py-2.5 rounded-lg bg-[#1A2230] border border-[rgba(255,255,255,0.08)] text-sm text-[#F0F4F8] placeholder:text-[#5C6778] outline-none focus:border-[rgba(0,229,192,0.3)] transition-colors"
                />
                {searchLoading && (
                  <CircleNotch size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6778] animate-spin" />
                )}
              </div>
              {toolSearch && searchResults.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5 max-h-48 overflow-y-auto" data-testid="onb-tool-search-results">
                  {searchResults.map((app) => {
                    const isSelected = selectedTools.has(app.name);
                    return (
                      <button key={app.slug || app.name} onClick={() => handleToolToggle(app.name)}
                        data-testid={`onb-tool-result-${(app.slug || app.name).toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs transition-all ${
                          isSelected
                            ? 'border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)] text-[#F0F4F8]'
                            : 'border-[rgba(255,255,255,0.08)] bg-[#1A2230] text-[#7D8896] hover:border-[rgba(255,255,255,0.12)]'
                        }`}>
                        <img src={app.logo} alt={app.name} className="w-5 h-5 rounded object-contain shrink-0"
                          onError={(e) => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
                        <span className="flex-1 text-left truncate">{app.name}</span>
                        {isSelected ? <Check size={10} className="text-[#00E5C0] shrink-0" /> : <PlusCircle size={12} className="text-[#5C6778] shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
              {toolSearch && !searchLoading && searchResults.length === 0 && (
                <p className="text-xs text-[#5C6778] mb-5">No apps matching "{toolSearch}"</p>
              )}

              {/* Ecosystem hint */}
              <div className="rounded-lg border border-[rgba(255,255,255,0.04)] bg-[#1A2230] px-3.5 py-2.5 mb-4" data-testid="onb-ecosystem-hint">
                <p className="text-[10px] text-[#5C6778] leading-relaxed">
                  {totalApps > 0 ? (
                    <>Search across <span className="text-[#7D8896] font-medium">{totalApps.toLocaleString()}+ apps</span> — connect any tool your business uses.</>
                  ) : (
                    <>Connect any of <span className="text-[#7D8896] font-medium">1,000+ apps</span> your business uses.</>
                  )}
                </p>
              </div>

              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={() => setStep(1)}
                  className="text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors flex items-center gap-1"
                >
                  <ArrowLeft size={12} /> Back
                </button>
                <button
                  onClick={handleToolsContinue}
                  data-testid="onb-tools-continue"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-semibold transition-colors"
                >
                  Continue <ArrowRight size={14} />
                </button>
              </div>
            </motion.div>
          )}

          {/* ═══════ STEP 3: Recommendations ═══════ */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-xs font-semibold mb-4">
                  <Sparkle size={14} weight="fill" /> Recommended for you
                </div>
                <h1 className="text-3xl font-bold text-[#E5E7EB] mb-2">Your AI workers</h1>
                <p className="text-sm text-[#9CA3AF]">We've picked the best workers for your business. Set them up in one click.</p>
              </div>

              <div className="space-y-3">
                {recommendations.map((rec, i) => {
                  const isCreated = created.includes(i);
                  const isCreating = creating === i;
                  return (
                    <div
                      key={i}
                      data-testid={`rec-${i}`}
                      className={`rounded-[14px] border p-5 transition-all duration-200 ${
                        rec.primary
                          ? 'bg-[#1E2D24] border-[#34D399] shadow-[0_0_0_1px_rgba(52,211,153,0.25)]'
                          : 'bg-[#1A2230] border-[rgba(255,255,255,0.08)]'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Robot size={16} weight="duotone" className="text-[#00E5C0]" />
                            <h3 className="text-white font-semibold text-sm">{rec.headline}</h3>
                          </div>
                          {rec.primary && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-semibold bg-[rgba(0,229,192,0.1)] text-[#00E5C0] mb-2">
                              <Lightning size={10} weight="fill" /> Start with this
                            </span>
                          )}
                          <p className="text-xs text-[#B0B8C4] mt-1">{rec.intent}</p>
                        </div>
                        <button
                          onClick={() => handleCreateWorker(rec, i)}
                          disabled={isCreated || isCreating}
                          className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                            isCreated
                              ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)]'
                              : rec.primary
                              ? 'bg-[#00E5C0] text-[#12161C] hover:bg-[#00CCB0]'
                              : 'bg-[rgba(255,255,255,0.06)] text-white border border-[rgba(255,255,255,0.08)] hover:bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          {isCreating ? (
                            <><CircleNotch size={14} className="animate-spin" /> Creating...</>
                          ) : isCreated ? (
                            <><CheckCircle size={14} weight="fill" /> Created</>
                          ) : (
                            <><Lightning size={14} weight="fill" /> Set up</>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mt-8">
                <button
                  onClick={() => setStep(2)}
                  className="text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors flex items-center gap-1"
                >
                  <ArrowLeft size={12} /> Back
                </button>
                <button
                  onClick={handleFinish}
                  data-testid="finish-onboarding"
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${
                    created.length > 0
                      ? 'bg-[#00E5C0] text-[#12161C] hover:bg-[#00CCB0]'
                      : 'bg-[rgba(255,255,255,0.06)] text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.1)]'
                  }`}
                >
                  {created.length > 0 ? 'Go to my workers' : 'Skip for now'}
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
